import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AccessibleBoard,
  type Impact,
  ImpactSummary,
  useBoardLabelCatalog,
  useSetBoardLabels,
} from '@/entities/collab';
import { queryKeys } from '@/shared/api';
import { FormError } from '@/shared/forms';
import { useIsTouchUi } from '@/shared/lib/media';
import { Button, Dialog, Popover, type PopoverTriggerProps } from '@/shared/ui';

import styles from './BoardLabelEditor.module.css';

type Target = Pick<AccessibleBoard, 'id' | 'name' | 'workspace_id' | 'labels'>;

interface Props {
  board: Target;
  trigger: (props: PopoverTriggerProps, open: boolean) => ReactNode;
  /** Controlled mode: the board card's menu opens the editor next to its "+" button. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/**
 * Board labels of ONE board, assigned from where the board is listed (the home page) - the
 * workspace's existing catalog, nothing home-specific. Saving always asks the server for the
 * consequences first (a label can be what grants access to the board): a change that would gain,
 * lose or alter anybody's access is shown and needs an explicit confirmation, exactly like the
 * board's own access dialog; the server re-checks the permission on the real save.
 */
export function BoardLabelEditor({ board, trigger, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  return (
    <Popover
      label={t('boardLabels.editorTitle', { name: board.name })}
      width={340}
      trigger={trigger}
      {...(open !== undefined && { open })}
      {...(onOpenChange && { onOpenChange })}
    >
      {(close) => <EditorPanel board={board} close={close} />}
    </Popover>
  );
}

/** The same editor as a dialog: for a menu item that has no "+" button to anchor a popover. */
export function BoardLabelsDialog({ board, onClose }: { board: Target; onClose: () => void }) {
  const { t } = useTranslation();
  return (
    <Dialog title={t('boardLabels.editorTitle', { name: board.name })} onClose={onClose}>
      <EditorPanel board={board} close={onClose} />
    </Dialog>
  );
}

function EditorPanel({ board, close }: { board: Target; close: () => void }) {
  const { t } = useTranslation();
  const searchId = useId();
  const touch = useIsTouchUi(); // a search to tap, not to type into: no unasked on-screen keyboard
  const client = useQueryClient();
  const catalog = useBoardLabelCatalog(board.workspace_id ?? '', board.workspace_id !== null);
  const save = useSetBoardLabels(board.id);
  const [query, setQuery] = useState('');
  // The person's CHANGES (labels they ticked / unticked), not a copy of the list: what somebody
  // else changed meanwhile (live `board.labels`, refreshed by realtime) is kept when this is saved.
  const [added, setAdded] = useState<ReadonlySet<string>>(new Set());
  const [removed, setRemoved] = useState<ReadonlySet<string>>(new Set());
  const [impact, setImpact] = useState<{ key: string; value: Impact } | null>(null);

  const labels = catalog.data ?? [];
  // A label deleted while this is open drops out of the selection (the server would refuse it).
  const known = useMemo(
    () => (catalog.data ? new Set(catalog.data.map((label) => label.id)) : null),
    [catalog.data],
  );
  const live = board.labels.map((label) => label.id);
  const isSelected = (id: string) => (live.includes(id) && !removed.has(id)) || added.has(id);
  // The list to store: the current labels minus what was unticked (their order kept), then the
  // newly ticked ones.
  const final = [
    ...live.filter((id) => !removed.has(id)),
    ...[...added].filter((id) => !live.includes(id) && (known === null || known.has(id))),
  ];
  const key = final.join(',');
  const changed = key !== live.join(',');
  const needle = query.trim().toLocaleLowerCase();
  const shown = labels.filter(
    (label) => !needle || label.name.toLocaleLowerCase().includes(needle),
  );
  const selectedCount = labels.filter((label) => isSelected(label.id)).length;

  const toggle = (id: string) => {
    const on = isSelected(id);
    const next = (set: ReadonlySet<string>, flag: boolean) => {
      const copy = new Set(set);
      if (flag) copy.add(id);
      else copy.delete(id);
      return copy;
    };
    if (live.includes(id)) setRemoved((current) => next(current, on));
    else setAdded((current) => next(current, !on));
  };
  const failed = () => {
    // The catalog may have changed (a label deleted, renamed): read it again.
    if (board.workspace_id) {
      void client.invalidateQueries({ queryKey: queryKeys.collab.boardLabels(board.workspace_id) });
    }
  };
  const commit = () => {
    save.mutate({ labelIds: final }, { onSuccess: close, onError: failed });
  };
  const submit = () => {
    if (impact?.key === key) {
      commit(); // the consequences shown are those of exactly this selection
      return;
    }
    // Ask first: nothing is written until the consequences are known.
    save.mutate(
      { labelIds: final, dryRun: true },
      {
        onSuccess: (result) => {
          const affects =
            result.impact.gained + result.impact.lost + result.impact.changed > 0 ||
            result.impact.hidden_boards > 0;
          if (affects) setImpact({ key, value: result.impact });
          else commit();
        },
        onError: failed,
      },
    );
  };
  const shownImpact = impact?.key === key ? impact.value : null;

  return (
    <div className={styles.panel} data-testid="board-label-editor">
      <label className="visually-hidden" htmlFor={searchId}>
        {t('boardLabels.search')}
      </label>
      <input
        id={searchId}
        type="search"
        className={styles.search}
        autoFocus={!touch}
        placeholder={t('boardLabels.search')}
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
        }}
      />
      {catalog.isPending && <p className={styles.note}>{t('common.loading')}</p>}
      {catalog.isError && <FormError error={catalog.error} />}
      {catalog.data && labels.length === 0 && (
        <p className={styles.note}>{t('boardLabels.noCatalog')}</p>
      )}
      <ul className={styles.options} aria-label={t('boardLabels.options')}>
        {shown.map((label) => (
          <li key={label.id}>
            <label className={styles.option}>
              <input
                type="checkbox"
                checked={isSelected(label.id)}
                onChange={() => {
                  toggle(label.id);
                }}
              />
              <span aria-hidden className={styles.dot} style={{ background: label.color }} />
              <span className={styles.name}>{label.name}</span>
              {label.access_controlling && (
                <span className={styles.grants}>{t('access.grantsAccess')}</span>
              )}
            </label>
          </li>
        ))}
        {catalog.data && labels.length > 0 && shown.length === 0 && (
          <li className={styles.note}>{t('common.noResults')}</li>
        )}
      </ul>
      {shownImpact && (
        <div className={styles.impact}>
          <p className={styles.warn} role="alert">
            {t('boardLabels.confirmAccess')}
          </p>
          <ImpactSummary impact={shownImpact} />
        </div>
      )}
      <FormError error={save.error} />
      <div className={styles.actions}>
        <span className={styles.selectedCount} aria-live="polite">
          {t('boardLabels.selectedCount', { count: selectedCount })}
        </span>
        <Button size="sm" variant="ghost" onClick={close}>
          {t('common.cancel')}
        </Button>
        <Button size="sm" variant="primary" disabled={!changed || save.isPending} onClick={submit}>
          {shownImpact ? t('boardLabels.confirmSave') : t('common.save')}
        </Button>
      </div>
    </div>
  );
}
